import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Home, Package, Camera, AlertTriangle, ClipboardList, Plus, Trash2 } from 'lucide-react';
import { Room, Project, Item, Photo, DamageReport } from '../types';
import { getProject } from '../services/projectService';
import { getRoom, deleteRoom } from '../services/roomService';
import { getItemsByRoom, deleteItem } from '../services/itemService';
import { getPhotosByRoom } from '../services/photoService';
import { getDamageReportsByProject } from '../services/damageReportService';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import ConfirmDialog from '../components/ConfirmDialog';
import ChecklistComponent from '../components/ChecklistComponent';
import PhotoList from '../components/PhotoList';
import CameraComponent from '../components/CameraComponent';
import { PageShell } from '../components/ui/PageShell';
import { SegmentedControl } from '../components/ui/SegmentedControl';
import { SwipeableRow } from '../components/ui/SwipeableRow';
import { StatusBadge } from '../components/ui/StatusBadge';
import { EmptyState } from '../components/ui/EmptyState';
import { FloatingActionButton } from '../components/ui/FloatingActionButton';
import { SkeletonList } from '../components/ui/Skeleton';
import { SearchBar } from '../components/ui/SearchBar';
import { useFullScreen } from '../components/Layout';

type Segment = 'objekt' | 'foton' | 'skador' | 'uppgifter';

const SEGMENTS = [
  { value: 'objekt', label: 'Objekt' },
  { value: 'foton', label: 'Foton' },
  { value: 'skador', label: 'Skador' },
  { value: 'uppgifter', label: 'Uppgifter' },
];

const formatDate = (timestamp: any) => {
  if (!timestamp) return '';
  try {
    const date = timestamp.toDate();
    return date.toLocaleDateString('sv-SE', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
};

const RoomWorkspacePage: React.FC = () => {
  const { projectId, roomId } = useParams<{ projectId: string; roomId: string }>();
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();
  const { setIsFullScreen } = useFullScreen();

  // Core data
  const [project, setProject] = useState<Project | null>(null);
  const [room, setRoom] = useState<Room | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [damageReports, setDamageReports] = useState<DamageReport[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSegment, setActiveSegment] = useState<Segment>(() => {
    if (roomId) {
      const stored = sessionStorage.getItem(`room-workspace-segment-${roomId}`);
      if (stored && ['objekt', 'foton', 'skador', 'uppgifter'].includes(stored)) {
        return stored as Segment;
      }
    }
    return 'objekt';
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteType, setDeleteType] = useState<'room' | 'item'>('item');
  const [showDeleteRoomConfirm, setShowDeleteRoomConfirm] = useState(false);
  const [photoLoadError, setPhotoLoadError] = useState<Record<string, boolean>>({});

  // Hide tab bar when camera is open
  useEffect(() => {
    setIsFullScreen(showCamera);
    return () => setIsFullScreen(false);
  }, [showCamera, setIsFullScreen]);

  // Persist active segment
  const handleSegmentChange = useCallback((value: string) => {
    const segment = value as Segment;
    setActiveSegment(segment);
    setSearchTerm('');
    if (roomId) {
      sessionStorage.setItem(`room-workspace-segment-${roomId}`, segment);
    }
  }, [roomId]);

  // Fetch all data
  const fetchData = useCallback(async () => {
    if (!projectId || !roomId) return;

    try {
      setLoading(true);
      const [fetchedProject, fetchedRoom, fetchedItems, fetchedPhotos, fetchedDamageReports] = await Promise.all([
        getProject(projectId),
        getRoom(roomId),
        getItemsByRoom(projectId, roomId),
        getPhotosByRoom(projectId, roomId),
        getDamageReportsByProject(projectId),
      ]);

      if (!fetchedProject) {
        setError('Projektet kunde inte hittas.');
        return;
      }

      setProject(fetchedProject);
      setRoom(fetchedRoom);
      setItems(fetchedItems);
      setPhotos(fetchedPhotos);

      // Filter damage reports: room-level (items in this room) or unscoped
      const roomItemIds = new Set(fetchedItems.map(i => i.id));
      const roomDamageReports = fetchedDamageReports.filter(r =>
        (r.itemId && roomItemIds.has(r.itemId)) || !r.itemId
      );
      setDamageReports(roomDamageReports);
      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Kunde inte hämta data. Försök igen senare.');
    } finally {
      setLoading(false);
    }
  }, [projectId, roomId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Item actions
  const handleDeleteItem = (itemId: string) => {
    if (!isOnline) {
      setError('Du är offline. Anslut till internet för att ta bort objekt.');
      return;
    }
    setDeleteType('item');
    setDeleteConfirmId(itemId);
  };

  const confirmDeleteItem = async () => {
    const itemId = deleteConfirmId;
    if (!itemId || !projectId) return;
    setDeleteConfirmId(null);
    try {
      await deleteItem(itemId, projectId);
      setItems(prev => prev.filter(i => i.id !== itemId));
    } catch (err) {
      console.error('Error deleting item:', err);
      setError('Kunde inte ta bort objektet.');
    }
  };

  // Room delete
  const handleDeleteRoom = () => {
    if (!isOnline) {
      setError('Du är offline.');
      return;
    }
    setShowDeleteRoomConfirm(true);
  };

  const confirmDeleteRoom = async () => {
    if (!roomId || !projectId) return;
    setShowDeleteRoomConfirm(false);
    try {
      await deleteRoom(roomId, projectId);
      navigate(`/projects/${projectId}/rooms`);
    } catch (err) {
      console.error('Error deleting room:', err);
      setError('Kunde inte ta bort rummet.');
    }
  };

  // Photo callbacks
  const handlePhotoDeleted = (photoId: string) => {
    setPhotos(prev => prev.filter(p => p.id !== photoId));
  };

  const refreshRoomPhotos = () => {
    if (projectId && roomId) {
      getPhotosByRoom(projectId, roomId).then(setPhotos).catch(console.error);
    }
  };

  const handleCameraClose = () => {
    setShowCamera(false);
    refreshRoomPhotos();
  };

  // Get photos for a specific item (for thumbnails)
  const getItemPhotos = (item: Item) => {
    return photos.filter(p => p.itemId === item.id);
  };

  // Get photos for a damage report
  const getReportPhotos = (report: DamageReport) => {
    if (!report.photoIds?.length) return [];
    return photos.filter(p => report.photoIds.includes(p.id));
  };

  // Filtered items
  const filteredItems = items.filter(item => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return item.name.toLowerCase().includes(s) ||
      item.description.toLowerCase().includes(s) ||
      item.category.toLowerCase().includes(s);
  });

  // Loading state
  if (loading) {
    return (
      <PageShell title="Laddar..." backTo={projectId ? `/projects/${projectId}/rooms` : '/'} backLabel="Rum">
        <div className="space-y-4 py-2">
          <SkeletonList count={4} />
        </div>
      </PageShell>
    );
  }

  // Error state
  if (error && (!project || !room)) {
    return (
      <PageShell title="Rum" backTo={projectId ? `/projects/${projectId}/rooms` : '/'} backLabel="Rum">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl text-sm mt-4">
          {error || 'Rummet kunde inte hittas.'}
        </div>
      </PageShell>
    );
  }

  if (!project || !room) return null;

  // Camera fullscreen
  if (showCamera) {
    return (
      <CameraComponent
        projectId={projectId!}
        roomId={roomId}
        onPhotoTaken={refreshRoomPhotos}
        onClose={handleCameraClose}
      />
    );
  }

  // FAB config per segment
  const fabConfig: Record<Segment, { icon: React.ReactNode; label: string; onClick: () => void } | null> = {
    objekt: {
      icon: <Plus size={22} className="text-white" />,
      label: 'Nytt objekt',
      onClick: () => navigate(`/projects/${projectId}/items/new?roomId=${roomId}`),
    },
    foton: {
      icon: <Camera size={22} className="text-white" />,
      label: 'Ta foto',
      onClick: () => setShowCamera(true),
    },
    skador: {
      icon: <Plus size={22} className="text-white" />,
      label: 'Ny skaderapport',
      onClick: () => navigate(`/projects/${projectId}/damage-reports/new`),
    },
    uppgifter: null,
  };

  const currentFab = fabConfig[activeSegment];

  return (
    <PageShell
      title={room.name}
      backTo={`/projects/${projectId}/rooms`}
      backLabel="Rum"
      headerRight={
        <button
          onClick={() => navigate(`/projects/${projectId}/rooms/${roomId}/edit`)}
          className="text-accent-600 dark:text-accent-400 text-[17px] active:opacity-50 transition-opacity"
        >
          Redigera
        </button>
      }
    >
      <div className="pb-24">
        {/* Segmented Control */}
        <div className="flex justify-center px-4 py-3 sticky top-[calc(var(--header-height,44px)+env(safe-area-inset-top,0px))] z-10 bg-[var(--bg-primary)]">
          <SegmentedControl
            segments={SEGMENTS}
            value={activeSegment}
            onChange={handleSegmentChange}
            className="w-full"
          />
        </div>

        {/* Error banner */}
        {error && (
          <div className="mx-4 mb-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl text-sm">
            {error}
          </div>
        )}

        {/* ===== OBJEKT SEGMENT ===== */}
        {activeSegment === 'objekt' && (
          <div className="px-4 space-y-2">
            {items.length > 3 && (
              <SearchBar
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Sök objekt..."
              />
            )}

            {filteredItems.length === 0 ? (
              <EmptyState
                icon={Package}
                title="Inga objekt"
                description={searchTerm ? 'Inga objekt matchar sökningen.' : `Lägg till objekt i ${room.name}.`}
                action={
                  !searchTerm && (
                    <button
                      onClick={() => navigate(`/projects/${projectId}/items/new?roomId=${roomId}`)}
                      className="inline-flex items-center gap-2 bg-accent-600 text-white px-4 py-2.5 text-sm font-medium rounded-xl active:bg-accent-700 transition-colors"
                    >
                      <Plus size={16} />
                      Lägg till objekt
                    </button>
                  )
                }
              />
            ) : (
              <div className="space-y-2">
                {filteredItems.map(item => {
                  const itemPhotos = getItemPhotos(item);
                  const hasPhoto = itemPhotos.length > 0 && !photoLoadError[itemPhotos[0]?.id];

                  return (
                    <SwipeableRow
                      key={item.id}
                      className="rounded-ios-card border border-[var(--border-default)]/80 overflow-hidden"
                      rightActions={[
                        {
                          label: 'Ta bort',
                          icon: <Trash2 size={18} />,
                          color: '#ef4444',
                          onClick: () => handleDeleteItem(item.id),
                        },
                      ]}
                    >
                      <Link
                        to={`/projects/${projectId}/items/${item.id}`}
                        className="block bg-[var(--bg-surface)] press-row overflow-hidden"
                      >
                        <div className="p-3">
                          <div className="flex gap-3">
                            {/* Thumbnail */}
                            <div className="flex-shrink-0">
                              {hasPhoto ? (
                                <div className="w-12 h-12 rounded-lg overflow-hidden bg-[var(--bg-surface-alt)]">
                                  <img
                                    src={itemPhotos[0].url}
                                    loading="lazy"
                                    alt=""
                                    className="w-full h-full object-cover"
                                    onError={() => setPhotoLoadError(prev => ({ ...prev, [itemPhotos[0].id]: true }))}
                                  />
                                </div>
                              ) : (
                                <div className="w-12 h-12 rounded-lg bg-[var(--bg-surface-alt)] flex items-center justify-center">
                                  <Package size={16} className="text-[var(--text-tertiary)]" />
                                </div>
                              )}
                            </div>

                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between">
                                <div className="min-w-0 flex-1">
                                  <h3 className="font-semibold text-[var(--text-primary)] text-[15px] leading-[20px] truncate">{item.name}</h3>
                                  {item.category && (
                                    <p className="text-xs text-[var(--text-secondary)] mt-0.5">{item.category}</p>
                                  )}
                                </div>
                                <StatusBadge status={item.priority} size="sm" className="ml-2 flex-shrink-0" />
                              </div>
                              {item.estimatedValue && (
                                <p className="text-xs text-[var(--text-secondary)] mt-1">
                                  {typeof item.estimatedValue === 'number' ? `${item.estimatedValue.toLocaleString('sv-SE')} kr` : item.estimatedValue}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </Link>
                    </SwipeableRow>
                  );
                })}
              </div>
            )}

            {/* Delete room at bottom */}
            <div className="pt-6">
              <button
                onClick={handleDeleteRoom}
                className="w-full py-3 text-center text-red-500 dark:text-red-400 text-[15px] font-medium bg-[var(--bg-surface)] rounded-ios-card border border-[var(--border-default)]/80 active:bg-red-50 dark:active:bg-red-900/20 transition-colors"
              >
                Ta bort rum
              </button>
            </div>
          </div>
        )}

        {/* ===== FOTON SEGMENT ===== */}
        {activeSegment === 'foton' && (
          <div className="px-4">
            {photos.length === 0 ? (
              <EmptyState
                icon={Camera}
                title="Inga foton"
                description={`Ta foton av ${room.name} för att dokumentera.`}
                action={
                  <button
                    onClick={() => setShowCamera(true)}
                    className="inline-flex items-center gap-2 bg-accent-600 text-white px-4 py-2.5 text-sm font-medium rounded-xl active:bg-accent-700 transition-colors"
                  >
                    <Camera size={16} />
                    Ta foto
                  </button>
                }
              />
            ) : (
              <PhotoList
                photos={photos}
                projectId={projectId!}
                onPhotoDeleted={handlePhotoDeleted}
                onAddPhotoClick={() => setShowCamera(true)}
                setPhotos={setPhotos as any}
                contextLabel={room.name}
              />
            )}
          </div>
        )}

        {/* ===== SKADOR SEGMENT ===== */}
        {activeSegment === 'skador' && (
          <div className="px-4 space-y-2">
            {damageReports.length === 0 ? (
              <EmptyState
                icon={AlertTriangle}
                title="Inga skaderapporter"
                description="Dokumentera skador som hittas i detta rum."
                action={
                  <button
                    onClick={() => navigate(`/projects/${projectId}/damage-reports/new`)}
                    className="inline-flex items-center gap-2 bg-accent-600 text-white px-4 py-2.5 text-sm font-medium rounded-xl active:bg-accent-700 transition-colors"
                  >
                    <Plus size={16} />
                    Ny skaderapport
                  </button>
                }
              />
            ) : (
              <div className="space-y-2">
                {damageReports.map(report => {
                  const reportPhotos = getReportPhotos(report);
                  const hasPhoto = reportPhotos.length > 0 && !photoLoadError[reportPhotos[0]?.id];

                  return (
                    <Link
                      key={report.id}
                      to={`/projects/${projectId}/damage-reports/${report.id}`}
                      className="block bg-[var(--bg-surface)] rounded-ios-card border border-[var(--border-default)]/80 p-3 press-row"
                    >
                      <div className="flex gap-3">
                        {/* Thumbnail */}
                        <div className="flex-shrink-0">
                          {hasPhoto ? (
                            <div className="w-12 h-12 rounded-lg overflow-hidden bg-[var(--bg-surface-alt)]">
                              <img
                                src={reportPhotos[0].url}
                                loading="lazy"
                                alt=""
                                className="w-full h-full object-cover"
                                onError={() => setPhotoLoadError(prev => ({ ...prev, [reportPhotos[0].id]: true }))}
                              />
                            </div>
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
                              <AlertTriangle size={16} className="text-red-500 dark:text-red-400" />
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <p className="text-sm font-medium text-[var(--text-primary)] line-clamp-2 flex-1">
                              {report.description}
                            </p>
                            <StatusBadge status={report.status} size="sm" className="ml-2 flex-shrink-0" />
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-xs text-[var(--text-secondary)]">
                            <span>{report.reportedBy}</span>
                            {report.reportedAt && (
                              <span>{formatDate(report.reportedAt)}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ===== UPPGIFTER SEGMENT ===== */}
        {activeSegment === 'uppgifter' && (
          <div className="px-4">
            <ChecklistComponent projectId={projectId!} />
          </div>
        )}
      </div>

      {/* Segment-aware FAB */}
      {currentFab && (
        <FloatingActionButton
          icon={currentFab.icon}
          onClick={currentFab.onClick}
          aria-label={currentFab.label}
        />
      )}

      {/* Delete item confirm */}
      <ConfirmDialog
        isOpen={deleteConfirmId !== null}
        title="Ta bort"
        message="Är du säker på att du vill ta bort detta objekt? Denna åtgärd kan inte ångras."
        confirmLabel="Ta bort"
        cancelLabel="Avbryt"
        variant="danger"
        onConfirm={confirmDeleteItem}
        onCancel={() => setDeleteConfirmId(null)}
      />

      {/* Delete room confirm */}
      <ConfirmDialog
        isOpen={showDeleteRoomConfirm}
        title="Ta bort rum"
        message={`Är du säker på att du vill ta bort "${room.name}"? Alla objekt och foton i rummet tas också bort.`}
        confirmLabel="Ta bort"
        cancelLabel="Avbryt"
        variant="danger"
        onConfirm={confirmDeleteRoom}
        onCancel={() => setShowDeleteRoomConfirm(false)}
      />
    </PageShell>
  );
};

export default RoomWorkspacePage;
